/**
 * useUndo - NPC 対戦専用の Undo 状態管理フック。
 *
 * 現在のゲームデザインでは Undo は広告報酬として提供しない。
 * UI 側でも非表示にしているが、古い導線から呼ばれても広告を起動しないよう
 * このフックの unlockViaAd は常に false を返す。
 *
 * 状態:
 *   - `isUnlocked`: 現行仕様では常に false。新しいゲームでも false に戻る。
 *   - `remainingUses`: UNDO_MAX_PER_PLAY - 既に使った回数。0 になると使用不可。
 *   - `isUnlocking`: 現行仕様では常に false。
 *
 * 永続化はしない（1 Play 毎にリセット）。ゲーム本体のスナップショット管理
 * （useGame の `snapshotRef` / `undoLastPair`）とは別責務で、ここでは
 * 「解放フラグ」と「残回数カウンタ」のみを持つ。
 *
 * 参考ルール:
 *   - `.claude/rules/antipatterns.md` (サイレントフォールバック禁止)
 *   - `.claude/rules/no-hardcode.md`
 */
import { useCallback, useState } from 'react'
import { UNDO_MAX_PER_PLAY } from '../config/undo'

interface UseUndoReturn {
  /** Undo が解放されているか。現行仕様では常に false。 */
  isUnlocked: boolean
  /** 現在の Play であと何回 Undo を使えるか。 */
  remainingUses: number
  /** Unlock 処理中か。現行仕様では常に false。 */
  isUnlocking: boolean
  /**
   * 互換用 API。現行仕様では広告を起動せず false を返す。
   */
  unlockViaAd: () => Promise<boolean>
  /**
   * Undo を 1 回消費する。残 0 なら false を返し、状態を変えない。
   * 実際の盤面ロールバックは呼び出し側（useGame）の責務。
   */
  consumeUndo: () => boolean
  /**
   * 新しいゲーム開始時に呼び、解放フラグと残回数を初期状態へ戻す。
   */
  reset: () => void
}

export function useUndo(): UseUndoReturn {
  const [isUnlocked, setIsUnlocked] = useState<boolean>(false)
  const [usedCount, setUsedCount] = useState<number>(0)
  const [isUnlocking, setIsUnlocking] = useState<boolean>(false)

  const remainingUses = Math.max(0, UNDO_MAX_PER_PLAY - usedCount)

  const unlockViaAd = useCallback(async (): Promise<boolean> => {
    return false
  }, [])

  const consumeUndo = useCallback((): boolean => {
    if (!isUnlocked) return false
    if (usedCount >= UNDO_MAX_PER_PLAY) return false
    setUsedCount((prev) => prev + 1)
    return true
  }, [isUnlocked, usedCount])

  const reset = useCallback((): void => {
    setIsUnlocked(false)
    setUsedCount(0)
    setIsUnlocking(false)
  }, [])

  return {
    isUnlocked,
    remainingUses,
    isUnlocking,
    unlockViaAd,
    consumeUndo,
    reset,
  }
}
